import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

const PROPERTY_TYPES = ["Apartment", "Villa", "Plot", "Penthouse", "Row House", "Office"];
const BHK_OPTIONS = ["1 BHK", "1.5 BHK", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "4+ BHK"];
const MAX_IMAGES = 10;
const MAX_BYTES = 5_000_000;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function ManualListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const fileRef = useRef(null);

  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({
    property_type: "Apartment",
    bhk: "2 BHK",
    price_lakhs: "",
    locality: "",
    notes: "",
    unit_variant: "",
    carpet_area_sqft: "",
    layout_notes: "",
    target_agent_slug: "",
  });
  const [images, setImages] = useState([]); // [{dataUrl, base64, mime, name}]
  const [consent, setConsent] = useState(false);
  const [publishNow, setPublishNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/agents", { params: { status: "active" } });
        setAgents(data.agents || []);
      } catch { /* ignore */ }
    })();
  }, [isOwner]);

  const onPickImages = async (e) => {
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;
    if (images.length + incoming.length > MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images per listing.`);
      return;
    }
    const next = [...images];
    for (const f of incoming) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 5 MB — skipped.`);
        continue;
      }
      if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) {
        toast.error(`${f.name} is not a supported image format.`);
        continue;
      }
      const dataUrl = await fileToBase64(f);
      const base64 = dataUrl.split(",", 2)[1];
      next.push({ dataUrl, base64, mime: f.type, name: f.name });
    }
    setImages(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => setImages((arr) => arr.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (publishNow && !consent) {
      toast.error("Tick the consent checkbox before publishing.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        property_type: form.property_type,
        bhk: form.bhk,
        price_lakhs: parseFloat(form.price_lakhs),
        locality: form.locality,
        notes: form.notes || undefined,
        unit_variant: form.unit_variant?.trim() || undefined,
        carpet_area_sqft: form.carpet_area_sqft ? parseFloat(form.carpet_area_sqft) : undefined,
        layout_notes: form.layout_notes || undefined,
        consent_confirmed: consent,
        publish_immediately: publishNow,
      };
      if (images.length) {
        payload.image_base64_list = images.map((i) => i.base64);
        payload.image_mime_list = images.map((i) => i.mime);
      }
      if (isOwner && form.target_agent_slug) {
        payload.target_agent_slug = form.target_agent_slug;
      }
      const { data } = await api.post("/listings/manual-create", payload);
      const verb = data.published ? "published live" : "created as draft";
      toast.success(`Listing ${data.listing_id} ${verb}.`);
      navigate(isOwner ? "/admin/agents" : "/dashboard/listings");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not create listing."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell>
      <div data-testid="manual-listing">
        <div className="overline text-sg-gold-deep">{isOwner ? "OWNER · CREATE LISTING" : "MANUAL CREATE"}</div>
        <h1 className="font-display text-4xl text-sg-navy mt-1">
          New <span className="font-serif-italic text-sg-gold-deep">listing</span>
        </h1>
        <p className="text-sg-mute mt-2 max-w-2xl">
          In addition to WhatsApp + OCR ingestion, you can hand-key a listing here. Tick the consent box + "Publish immediately" to go live on your branded portal without an extra step.
        </p>

        <form onSubmit={onSubmit} className="mt-8 bg-white border border-sg-line rounded-2xl p-7 max-w-3xl space-y-5" data-testid="manual-listing-form">
          {isOwner && (
            <div>
              <Label htmlFor="ml-agent" className="text-xs uppercase tracking-wider text-sg-mute">Create on behalf of</Label>
              <select
                id="ml-agent"
                required
                value={form.target_agent_slug}
                onChange={(e) => setForm({ ...form, target_agent_slug: e.target.value })}
                className="mt-1 w-full h-11 border border-sg-line rounded-md bg-sg-cream-soft px-3 text-sm"
                data-testid="manual-target-agent"
              >
                <option value="">— select agent —</option>
                {agents.map((a) => (
                  <option key={a.user_id} value={a.slug}>{a.name} · {a.email}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="ml-type" className="text-xs uppercase tracking-wider text-sg-mute">Property type</Label>
              <select id="ml-type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                      className="mt-1 w-full h-11 border border-sg-line rounded-md bg-sg-cream-soft px-3 text-sm" data-testid="manual-property-type">
                {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="ml-bhk" className="text-xs uppercase tracking-wider text-sg-mute">BHK</Label>
              <select id="ml-bhk" value={form.bhk} onChange={(e) => setForm({ ...form, bhk: e.target.value })}
                      className="mt-1 w-full h-11 border border-sg-line rounded-md bg-sg-cream-soft px-3 text-sm" data-testid="manual-bhk">
                {BHK_OPTIONS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="ml-price" className="text-xs uppercase tracking-wider text-sg-mute">Price (₹ lakhs)</Label>
              <Input id="ml-price" required type="number" min="1" step="0.1" value={form.price_lakhs} onChange={(e) => setForm({ ...form, price_lakhs: e.target.value })}
                     className="mt-1 bg-sg-cream-soft" data-testid="manual-price" placeholder="e.g. 85" />
            </div>
            <div>
              <Label htmlFor="ml-locality" className="text-xs uppercase tracking-wider text-sg-mute">Locality</Label>
              <Input id="ml-locality" required value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })}
                     className="mt-1 bg-sg-cream-soft" data-testid="manual-locality" placeholder="e.g. Baner, Kharadi" />
            </div>
            <div>
              <Label htmlFor="ml-carpet" className="text-xs uppercase tracking-wider text-sg-mute">Carpet area (sq ft)</Label>
              <Input id="ml-carpet" type="number" min="100" step="1" value={form.carpet_area_sqft} onChange={(e) => setForm({ ...form, carpet_area_sqft: e.target.value })}
                     className="mt-1 bg-sg-cream-soft" data-testid="manual-carpet" placeholder="optional" />
            </div>
            <div>
              <Label htmlFor="ml-layout" className="text-xs uppercase tracking-wider text-sg-mute">Layout notes</Label>
              <Input id="ml-layout" value={form.layout_notes} onChange={(e) => setForm({ ...form, layout_notes: e.target.value })}
                     className="mt-1 bg-sg-cream-soft" data-testid="manual-layout" placeholder="e.g. East-facing, 2T, balcony" />
            </div>
          </div>

          <div>
            <Label htmlFor="ml-variant" className="text-xs uppercase tracking-wider text-sg-mute">Unit variant <span className="text-sg-mute/70 normal-case tracking-normal">(e.g. Grand, Prime, Duplex, Supreme)</span></Label>
            <Input id="ml-variant" value={form.unit_variant} onChange={(e) => setForm({ ...form, unit_variant: e.target.value })}
                   className="mt-1 bg-sg-cream-soft" data-testid="manual-variant" placeholder="optional — surfaces as a filter pill on your branded portal" />
          </div>

          <div>
            <Label htmlFor="ml-notes" className="text-xs uppercase tracking-wider text-sg-mute">Notes (no PII)</Label>
            <Textarea id="ml-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-1 bg-sg-cream-soft min-h-[100px]" data-testid="manual-notes"
                      placeholder="Public description — avoid owner names, phone numbers, unit numbers." />
          </div>

          {/* Image picker */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-sg-mute">Photos · max {MAX_IMAGES} · ≤ 5 MB each</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="manual-images-grid">
              {images.map((img, idx) => (
                <div key={idx} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-sg-line bg-sg-cream-soft" data-testid={`manual-image-${idx}`}>
                  <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-sg-navy flex items-center justify-center shadow"
                    aria-label="Remove image"
                    data-testid={`manual-image-remove-${idx}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="aspect-[4/3] rounded-xl border-2 border-dashed border-sg-gold/40 bg-sg-cream-soft hover:bg-sg-gold/5 text-sg-gold-deep flex flex-col items-center justify-center gap-1 transition-colors"
                  data-testid="manual-image-add"
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-xs font-medium">Add photos</span>
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={onPickImages}
              className="hidden"
              data-testid="manual-image-input"
            />
          </div>

          {/* Data-Ownership + RERA Consent + publish toggle */}
          <div className="bg-sg-cream-soft border border-sg-line rounded-xl p-4 space-y-3" data-testid="manual-consent-panel">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-sg-gold accent-sg-gold-deep"
                data-testid="manual-consent-checkbox"
              />
              <span className="text-sm text-sg-navy leading-relaxed">
                I confirm that I own this listing or have explicit authorization from the property owner to advertise it. I verify that all data provided complies with RERA regulations and is free of unauthorized personal data.
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={publishNow}
                onChange={(e) => setPublishNow(e.target.checked)}
                className="w-4 h-4 rounded border-sg-gold accent-sg-gold-deep"
                data-testid="manual-publish-now"
              />
              <span className="text-sm text-sg-navy">Publish immediately to my branded portal (otherwise saved as Draft)</span>
            </label>
          </div>

          <Button type="submit" disabled={submitting || (publishNow && !consent)} size="lg" className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-12 px-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="manual-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            {publishNow ? "Publish listing" : "Save as draft"}
          </Button>
        </form>
      </div>
    </DashboardShell>
  );
}
